import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "@/components/Shell";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { User, Mail, KeyRound, Shield, Trash2, LogOut, Award } from "lucide-react";

const LEGAL = [
  ["Disclaimer", "/disclaimer"], ["Terms of service", "/terms"], ["Privacy policy", "/privacy"],
  ["Cookies policy", "/cookies"], ["Accessibility", "/accessibility"], ["Acceptable use policy", "/acceptable-use"],
];

export default function Profile() {
  const { user, org, logout } = useAuth();
  const nav = useNavigate();
  const [em, setEm] = useState({ new_email: "", current_password: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  // F7 (Patch 65/66) — Build Badge on Profile: reuses the existing per-project build-badge.svg endpoint.
  const [projects, setProjects] = useState([]);
  const API = process.env.REACT_APP_BACKEND_URL;
  useEffect(() => {
    if (org?.id) api.get(`/orgs/${org.id}/projects`)
      .then(r => setProjects((r.data || []).filter(p => !p.pending_delete && !p.archived))).catch(() => {});
  }, [org]);

  const changeEmail = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-email", em);
      toast.success(`Verification sent to ${data.email}. Confirm it to finish the change.`);
      setEm({ new_email: "", current_password: "" });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const changePassword = async () => {
    setBusy(true);
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password updated.");
      setPw({ current_password: "", new_password: "" });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const card = { padding: 22, marginTop: 18 };
  const h3 = { marginTop: 0, fontSize: 18, display: "flex", alignItems: "center", gap: 8 };
  return (
    <Shell active="profile">
      <h1 className="k-heading" style={{ fontSize: 30, margin: 0 }} data-testid="profile-title">Profile</h1>

      <div className="k-container" data-testid="profile-account" style={card}>
        <h3 className="k-heading" style={h3}><User size={16} /> Account</h3>
        <div className="k-stack k-stack-3">
          <div><span className="k-muted" style={{ fontSize: 12 }}>Username</span>
            <div className="k-heading" data-testid="profile-username">{user?.name || "—"}</div></div>
          <div><span className="k-muted" style={{ fontSize: 12 }}>Email</span>
            <div className="k-heading" data-testid="profile-email">{user?.email}{user?.email_verified === false ? " (unverified)" : ""}</div></div>
          <div><span className="k-muted" style={{ fontSize: 12 }}>Password</span>
            <div className="k-heading" data-testid="profile-password-mask">••••••••••</div></div>
        </div>
        <button className="k-btn k-btn-ghost" data-testid="profile-signout" style={{ marginTop: 14 }} onClick={async () => { await logout(); nav("/login"); }}><LogOut size={14} /> Sign out</button>
      </div>

      <div className="k-container" data-testid="profile-change-email" style={card}>
        <h3 className="k-heading" style={h3}><Mail size={16} /> Change email</h3>
        <div className="k-stack k-stack-3">
          <input className="k-control" data-testid="ce-new-email" type="email" placeholder="New email address"
            value={em.new_email} onChange={e => setEm(v => ({ ...v, new_email: e.target.value }))} />
          <input className="k-control" data-testid="ce-password" type="password" placeholder="Current password"
            value={em.current_password} onChange={e => setEm(v => ({ ...v, current_password: e.target.value }))} />
          <button className="k-btn k-btn-primary" data-testid="ce-submit" disabled={busy || !em.new_email || !em.current_password}
            onClick={changeEmail}>Send verification & change</button>
        </div>
        <p className="k-muted" style={{ fontSize: 12, marginTop: 8 }}>We email a verification link to the new address; the change completes once you confirm it.</p>
      </div>

      <div className="k-container" data-testid="profile-change-password" style={card}>
        <h3 className="k-heading" style={h3}><KeyRound size={16} /> Change password</h3>
        <div className="k-stack k-stack-3">
          <input className="k-control" data-testid="cp-current" type="password" placeholder="Current password"
            value={pw.current_password} onChange={e => setPw(v => ({ ...v, current_password: e.target.value }))} />
          <input className="k-control" data-testid="cp-new" type="password" placeholder="New password (at least 8 characters)"
            value={pw.new_password} onChange={e => setPw(v => ({ ...v, new_password: e.target.value }))} />
          <button className="k-btn k-btn-primary" data-testid="cp-submit" disabled={busy || pw.new_password.length < 8 || !pw.current_password}
            onClick={changePassword}>Update password</button>
        </div>
      </div>

      <div className="k-container" data-testid="profile-build-badges" style={card}>
        <h3 className="k-heading" style={h3}><Award size={16} /> Build badges</h3>
        <p className="k-muted" style={{ fontSize: 13, marginBottom: 10 }}>A non-cash badge for each app you've built — link to or embed it anywhere.</p>
        {projects.length === 0 ? <p className="k-muted" style={{ fontSize: 13 }}>No apps yet — your first build earns its badge.</p> : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {projects.map(p => (
              <a key={p.id} data-testid={`profile-badge-${p.id}`} href={`${API}/api/projects/${p.id}/build-badge.svg`} target="_blank" rel="noreferrer" title={p.name}
                style={{ display: "inline-flex", alignItems: "center" }}>
                <img src={`${API}/api/projects/${p.id}/build-badge.svg`} alt={`Build badge — ${p.name}`} style={{ height: 30 }} />
              </a>
            ))}
          </div>
        )}
      </div>

      <div className="k-container" data-testid="profile-apps" style={card}>
        <h3 className="k-heading" style={h3}><Trash2 size={16} /> Your apps</h3>
        <p className="k-muted" style={{ fontSize: 13, marginBottom: 10 }}>Manage, archive or delete your apps. Deleting an app has a 24-hour undo grace period.</p>
        <button className="k-btn k-btn-ghost" data-testid="profile-manage-apps" onClick={() => nav("/workspace/apps")}>Manage &amp; delete apps</button>
      </div>

      <div className="k-container" data-testid="profile-legal" style={card}>
        <h3 className="k-heading" style={h3}><Shield size={16} /> Legal &amp; trust</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          {LEGAL.map(([label, to]) => (
            <button key={to} className="k-link" data-testid={`profile-legal-${to.slice(1)}`}
              onClick={() => nav(to)} style={{ fontSize: 13 }}>{label}</button>
          ))}
        </div>
      </div>
    </Shell>
  );
}
